import { Fragment, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link, useNavigate, useParams } from 'react-router'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { SkeletonFields, SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  CabCarrier,
  CabCarrierContactInfo,
  CabCrash,
  CabSevereViolation,
  CabVehicle,
  useCabCrashes,
  useCabLead,
  useCabLookup,
  useCabSevereViolations,
  useCabVehicles,
  useImportLeadFromCab,
} from '../../../../models/cab'
import { useSalesLead } from '../../../../models/salesLead'
import { CabReportButton } from '../Internal/SalesLeads/CabReportButton'
import { LookupField } from './LookupField'

const VEHICLES_PAGE_SIZE = 50
const LAST_DOT_STORAGE_KEY = 'cab.lastDot'

export function Cab() {
  const navigate = useNavigate()
  const { dotNumber } = useParams<{ dotNumber?: string }>()
  const [input, setInput] = useState(dotNumber ?? '')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (dotNumber) {
      setInput(dotNumber)
      return
    }
    const stored = localStorage.getItem(LAST_DOT_STORAGE_KEY)
    if (stored) setInput(stored)
  }, [dotNumber])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = input.trim()
    if (!/^\d+$/.test(trimmed)) {
      setError('DOT number must be numeric')
      return
    }
    setError(null)
    localStorage.setItem(LAST_DOT_STORAGE_KEY, trimmed)
    navigate(`/insure/internal/cab/${trimmed}`)
  }

  return (
    <>
      <Helmet>
        <title>CAB Lookup</title>
      </Helmet>
      <div className="flex flex-col gap-5">
        <div className="text-2xl font-semibold">CAB Lookup</div>
        <form onSubmit={handleSubmit} className="flex flex-col gap-1">
          <label htmlFor="dot" className="text-sm font-medium">
            DOT Number
          </label>
          <div className="flex items-center gap-3">
            <Input
              id="dot"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="e.g. 3300318"
              className="w-64"
              autoFocus
            />
            <Button type="submit">Look up</Button>
          </div>
          {error && <span className="text-destructive text-xs">{error}</span>}
        </form>

        {dotNumber && <CabResults dotNumber={dotNumber} />}
      </div>
    </>
  )
}

function CabResults({ dotNumber }: { dotNumber: string }) {
  const lookup = useCabLookup(dotNumber)
  const vehicles = useCabVehicles(dotNumber)
  const crashes = useCabCrashes(dotNumber)
  const severeViolations = useCabSevereViolations(dotNumber)

  return (
    <div className="flex flex-col gap-4">
      <CarrierInfoCard
        isLoading={lookup.isLoading}
        carrier={lookup.data?.carrier ?? null}
        dotNumber={dotNumber}
      />
      <ContactsCard isLoading={lookup.isLoading} contacts={lookup.data?.contacts ?? null} />
      <AuthorityHistoryCard isLoading={lookup.isLoading} rows={lookup.data?.authorityHistory ?? null} />
      <UnitsCard isLoading={vehicles.isLoading} vehicles={vehicles.data ?? []} />
      <CrashesCard isLoading={crashes.isLoading} crashes={crashes.data ?? []} />
      <SevereViolationsCard isLoading={severeViolations.isLoading} violations={severeViolations.data ?? []} />
      <SectionCard
        title="Current Insurance"
        isLoading={lookup.isLoading}
        data={lookup.data?.insurance ?? null}
      />
      <SectionCard
        title="Insurance History"
        isLoading={lookup.isLoading}
        data={lookup.data?.insuranceHistory ?? null}
      />
    </div>
  )
}

function CarrierInfoCard({
  isLoading,
  carrier,
  dotNumber,
}: {
  isLoading: boolean
  carrier: CabCarrier | null
  dotNumber?: string
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Carrier Info</CardTitle>
          {!isLoading && carrier && dotNumber && <LeadImportAction key={dotNumber} dotNumber={dotNumber} />}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonFields fields={12} />
        ) : !carrier ? (
          <div className="text-muted-foreground text-sm">Not found in CAB.</div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <LookupField label="Legal Name" value={carrier.legalName} />
            <LookupField label="DBA" value={carrier.dbaName} />
            <LookupField label="DOT" value={carrier.dotNumber} />
            <LookupField label="Entity Type" value={carrier.entityType} />
            <LookupField label="Operating Status" value={carrier.operatingStatus} />
            <LookupField label="Power Units" value={carrier.powerUnits} />
            <LookupField label="Drivers" value={carrier.drivers} />
            <LookupField label="MCS-150 Mileage" value={carrier.mcs150Mileage} />
            <LookupField
              label="Years in Business"
              value={carrier.yearsSinceReinstatement ?? carrier.yearsInBusiness}
              note={
                carrier.yearsSinceReinstatement !== null &&
                carrier.yearsSinceReinstatement !== carrier.yearsInBusiness
                  ? 'since reinstatement'
                  : undefined
              }
            />
            <LookupField label="Cargo Carried" value={carrier.cargoCarried?.join(', ')} />
            <LookupField
              label="Operation Classification"
              value={carrier.operationClassification?.join(', ')}
            />
          </dl>
        )}
      </CardContent>
    </Card>
  )
}

function ContactsCard({
  isLoading,
  contacts,
}: {
  isLoading: boolean
  contacts: CabCarrierContactInfo | null
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>All Contacts</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonFields />
        ) : !contacts ? (
          <div className="text-muted-foreground text-sm">Not found in CAB.</div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <LookupField label="Name" value={contacts.name} />
            <LookupField label="Title" value={contacts.title} />
            <LookupField label="Email" value={contacts.email} />
            <LookupField label="Phone" value={contacts.phone} />
            <LookupField label="Address" value={contacts.address} />
            <LookupField
              label="City / State / Zip"
              value={[contacts.city, contacts.state, contacts.zipCode].filter(Boolean).join(', ')}
            />
          </dl>
        )}
      </CardContent>
    </Card>
  )
}

function SectionCard({
  title,
  isLoading,
  data,
}: {
  title: string
  isLoading: boolean
  data: Record<string, unknown> | null
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : !data ? (
          <div className="text-muted-foreground text-sm">Not found in CAB.</div>
        ) : (
          <DataView value={data} />
        )}
      </CardContent>
    </Card>
  )
}

function DataView({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>
  }
  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground">—</span>
    const allObjects = value.every((v) => v !== null && typeof v === 'object' && !Array.isArray(v))
    if (allObjects) {
      return <ObjectArrayTable rows={value as Record<string, unknown>[]} />
    }
    return <span>{value.map((v) => String(v)).join(', ')}</span>
  }
  if (typeof value === 'object') {
    return <KeyValueTable data={value as Record<string, unknown>} />
  }
  return <span>{String(value)}</span>
}

function KeyValueTable({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data)
  if (entries.length === 0) {
    return <span className="text-muted-foreground">—</span>
  }

  const primitives: [string, unknown][] = []
  const sections: [string, unknown][] = []
  for (const entry of entries) {
    if (isStructured(entry[1])) sections.push(entry)
    else primitives.push(entry)
  }

  return (
    <div className="flex flex-col gap-3">
      {primitives.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-sm">
          {primitives.map(([key, value]) => (
            <Fragment key={key}>
              <dt className="text-muted-foreground font-medium">{humanizeKey(key)}</dt>
              <dd>
                <DataView value={value} />
              </dd>
            </Fragment>
          ))}
        </dl>
      )}
      {sections.map(([key, value]) => (
        <div key={key} className="flex flex-col gap-1">
          <div className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
            {humanizeKey(key)}
          </div>
          <DataView value={value} />
        </div>
      ))}
    </div>
  )
}

function isStructured(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (Array.isArray(value)) {
    if (value.length === 0) return false
    return value.every((v) => v !== null && typeof v === 'object' && !Array.isArray(v))
  }
  return typeof value === 'object'
}

const SECTION_HEADER_KEYS = ['docpre', 'docnum', 'docket']

function ObjectArrayTable({ rows }: { rows: Record<string, unknown>[] }) {
  const hasSectionHeader = rows.some((r) => SECTION_HEADER_KEYS.some((k) => k in r))

  if (hasSectionHeader) {
    return (
      <div className="flex flex-col gap-4">
        {rows.map((row, i) => {
          const headerEntries = SECTION_HEADER_KEYS.filter((k) => k in row).map((k) => [k, row[k]] as const)
          const bodyEntries = Object.entries(row).filter(([k]) => !SECTION_HEADER_KEYS.includes(k))
          return (
            <div key={i} className="flex flex-col gap-2">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm font-medium">
                {headerEntries.map(([k, v]) => (
                  <span key={k}>
                    <span className="text-muted-foreground mr-1">{humanizeKey(k)}:</span>
                    {v == null || v === '' ? '—' : String(v)}
                  </span>
                ))}
              </div>
              <KeyValueTable data={Object.fromEntries(bodyEntries)} />
            </div>
          )
        })}
      </div>
    )
  }

  const columns = Array.from(new Set(rows.flatMap((r) => Object.keys(r))))
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {columns.map((col) => (
            <TableHead key={col}>{humanizeKey(col)}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {columns.map((col) => (
              <TableCell key={col} className="align-top text-sm">
                <DataView value={row[col]} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
}

function UnitsCard({ isLoading, vehicles }: { isLoading: boolean; vehicles: CabVehicle[] }) {
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [vehicles])

  const totalPages = Math.max(1, Math.ceil(vehicles.length / VEHICLES_PAGE_SIZE))
  const start = (page - 1) * VEHICLES_PAGE_SIZE
  const visible = vehicles.slice(start, start + VEHICLES_PAGE_SIZE)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Units ({vehicles.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : vehicles.length === 0 ? (
          <div className="text-muted-foreground text-sm">No vehicles found.</div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>VIN</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Make</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>Vehicle Type</TableHead>
                  <TableHead>Weight Class</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((v, i) => (
                  <TableRow key={v.vin ?? `unknown-${start + i}`}>
                    <TableCell className="font-mono text-xs">{v.vin}</TableCell>
                    <VehicleCell cab={v.year} nhtsa={v.nhtsa?.year} />
                    <VehicleCell cab={v.make} nhtsa={v.nhtsa?.make} />
                    <VehicleCell cab={v.model} nhtsa={v.nhtsa?.model} />
                    <VehicleCell cab={v.vehicleType} nhtsa={v.nhtsa?.vehicleClass} />
                    <VehicleCell cab={null} nhtsa={v.nhtsa?.weightClass} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <div className="mt-3 flex items-center justify-end gap-2 text-sm">
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CrashesCard({ isLoading, crashes }: { isLoading: boolean; crashes: CabCrash[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Crashes ({crashes.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : crashes.length === 0 ? (
          <div className="text-muted-foreground text-sm">No crashes found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Fatalities</TableHead>
                <TableHead>Injuries</TableHead>
                <TableHead>Tow Away</TableHead>
                <TableHead>Vehicles</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {crashes.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="text-sm">{c.date ?? '—'}</TableCell>
                  <TableCell className="text-sm">
                    {[c.location, c.city, c.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-sm">{c.fatalities}</TableCell>
                  <TableCell className="text-sm">{c.injuries}</TableCell>
                  <TableCell className="text-sm">{c.towAway ? 'Yes' : 'No'}</TableCell>
                  <TableCell className="text-sm">{c.vehiclesInCrash}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function SevereViolationsCard({
  isLoading,
  violations,
}: {
  isLoading: boolean
  violations: CabSevereViolation[]
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Severe Violations ({violations.length})</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : violations.length === 0 ? (
          <div className="text-muted-foreground text-sm">No severe violations found.</div>
        ) : (
          <ObjectArrayTable rows={violations as Record<string, unknown>[]} />
        )}
      </CardContent>
    </Card>
  )
}

function VehicleCell({
  cab,
  nhtsa,
}: {
  cab: string | number | null | undefined
  nhtsa: string | number | undefined
}) {
  const cabValue = cab == null || cab === '' ? null : cab
  return (
    <TableCell>
      <div className="flex flex-col gap-1">
        <span>{cabValue ?? '—'}</span>
        {nhtsa != null && (
          <span className="text-muted-foreground flex items-center gap-1 text-xs">
            <Badge variant="secondary">NHTSA</Badge>
            {nhtsa}
          </span>
        )}
      </div>
    </TableCell>
  )
}

function AuthorityHistoryCard({
  isLoading,
  rows,
}: {
  isLoading: boolean
  rows: Record<string, unknown>[] | null
}) {
  const actions =
    rows?.flatMap((docket) => {
      const acts = (docket.actions as Record<string, unknown>[] | undefined) ?? []
      return acts.map((a) => ({
        type: a.type ?? '—',
        action: a.action ?? '—',
        date: a.date ?? '—',
        disposition: a.disposition ?? null,
        disposition_date: a.disposition_date ?? null,
      }))
    }) ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle>Authority History</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <SkeletonRows rows={4} />
        ) : actions.length === 0 ? (
          <div className="text-muted-foreground text-sm">No authority history found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Auth Type</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Disposition</TableHead>
                <TableHead>Disposition Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {actions.map((a, i) => (
                <TableRow key={i}>
                  <TableCell className="text-sm">{String(a.type)}</TableCell>
                  <TableCell className="text-sm font-medium">{String(a.action)}</TableCell>
                  <TableCell className="text-sm">{String(a.date)}</TableCell>
                  <TableCell className="text-sm">{a.disposition ? String(a.disposition) : '—'}</TableCell>
                  <TableCell className="text-sm">
                    {a.disposition_date ? String(a.disposition_date) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function LeadImportAction({ dotNumber }: { dotNumber: string }) {
  const existingLead = useCabLead(dotNumber)
  const importLead = useImportLeadFromCab()
  const leadId = existingLead.data?.leadId
  const leadQuery = useSalesLead(leadId ?? '')

  if (leadId) {
    return (
      <div className="flex flex-col items-end gap-2">
        <Link
          to={`/insure/internal/sales-leads/${leadId}`}
          className="text-ink text-sm font-medium underline"
        >
          View Existing Lead →
        </Link>
        {leadQuery.data && <CabReportButton lead={leadQuery.data} className="w-auto" />}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        disabled={existingLead.isLoading}
        loading={importLead.isPending ? 'Creating…' : undefined}
        onClick={() => importLead.mutate(dotNumber)}
      >
        Create Lead
      </Button>
      {importLead.isError && <span className="text-destructive text-xs">Failed to create lead</span>}
    </div>
  )
}
